import React from 'react';
import { UserMinusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CollaborationUser } from '../../edgedocs/types/Collaboration.types';
import { UserAvatar } from './UserAvatar';

interface RemoveCollaboratorModalProps {
  isOpen: boolean;
  collaborator: CollaborationUser | null;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  isProcessing?: boolean;
}

export const RemoveCollaboratorModal: React.FC<RemoveCollaboratorModalProps> = ({
  isOpen,
  collaborator,
  onConfirm,
  onCancel,
  isProcessing = false,
}) => {
  if (!isOpen || !collaborator) return null;

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Failed to remove collaborator:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity lex-overlay-enter">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal */}
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
          {/* Content */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              {/* Warning Icon */}
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                <UserMinusIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>

              {/* Content */}
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Remove Collaborator
                </h3>
                
                {/* User Info */}
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <UserAvatar
                      user={collaborator}
                      size="md"
                      showStatus={false}
                      showRole={false}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {collaborator.name || collaborator.email}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {collaborator.email}
                      </div>
                      <div className="text-xs text-lex-accent capitalize">
                        {collaborator.role}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Warning Message */}
                <div className="mt-4 flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <p className="font-medium mb-1">This action will:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-500 dark:text-gray-400">
                      <li>Remove their access to this document</li>
                      <li>Stop them from receiving collaboration notifications</li>
                      <li>Preserve their existing comments and changes</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to remove <strong>{collaborator.name || collaborator.email}</strong> from this document? 
                    They can be re-invited later if needed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isProcessing}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
            >
              {isProcessing ? 'Removing...' : 'Remove Collaborator'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
